import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/common/Layout';
import { sostenibilidadService } from '../services/sostenibilidad';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Leaf, Factory, Building2, Sparkles, DollarSign, Recycle, Gauge, Settings2 } from 'lucide-react';

const TIPO_LABEL = {
    energia: 'Energía',
    agua: 'Agua',
    gas: 'Gas',
    combustible: 'Combustible',
    residuos: 'Residuos',
    otro: 'Otro',
};

const ALCANCE_DESC = {
    1: 'Combustión directa (combustible)',
    2: 'Energía eléctrica comprada',
    3: 'Cadena de suministro (agua, residuos)',
};

const ConfiguracionIntensidadModal = ({ onCerrar, onGuardado }) => {
    const [config, setConfig] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [guardandoAlmacen, setGuardandoAlmacen] = useState(null);

    useEffect(() => {
        sostenibilidadService.listarConfiguracionAlmacenes()
            .then(setConfig)
            .catch(() => toast.error('Error al cargar la configuración'))
            .finally(() => setCargando(false));
    }, []);

    const actualizarCampo = (almacen, campo, valor) => {
        setConfig((prev) => prev.map((c) => c.almacen === almacen ? { ...c, [campo]: valor } : c));
    };

    const guardar = async (item) => {
        setGuardandoAlmacen(item.almacen);
        try {
            await sostenibilidadService.guardarConfiguracionAlmacen(item.almacen, item.area_m2, item.colaboradores);
            toast.success(`${item.almacen} actualizado`);
            onGuardado();
        } catch {
            toast.error('Error al guardar');
        } finally {
            setGuardandoAlmacen(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-5 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Settings2 className="h-5 w-5 text-green-600" />
                        Configuración de Intensidad por Almacén
                    </h2>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="px-6 py-5">
                    <p className="text-sm text-gray-500 mb-4">
                        Área (m²) y colaboradores por almacén, usados para calcular los indicadores de intensidad
                        (kWh/m², litros de agua/colaborador/día, kg de residuos/colaborador).
                    </p>
                    {cargando ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {config.map((item) => (
                                <div key={item.almacen} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                                    <span className="flex-1 text-sm font-medium text-gray-800">{item.almacen}</span>
                                    <input
                                        type="number" min="0" step="0.1"
                                        placeholder="Área m²"
                                        value={item.area_m2 ?? ''}
                                        onChange={(e) => actualizarCampo(item.almacen, 'area_m2', e.target.value)}
                                        className="w-28 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                                    />
                                    <input
                                        type="number" min="0" step="1"
                                        placeholder="Colaboradores"
                                        value={item.colaboradores ?? ''}
                                        onChange={(e) => actualizarCampo(item.almacen, 'colaboradores', e.target.value)}
                                        className="w-32 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                                    />
                                    <button
                                        onClick={() => guardar(item)}
                                        disabled={guardandoAlmacen === item.almacen}
                                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60"
                                    >
                                        Guardar
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SostenibilidadPage = () => {
    const { isAdmin } = useAuth();
    const [periodos, setPeriodos] = useState([]);
    const [periodo, setPeriodo] = useState('');
    const [resumen, setResumen] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [recos, setRecos] = useState(null);
    const [cargandoRecos, setCargandoRecos] = useState(false);
    const [intensidad, setIntensidad] = useState([]);
    const [mostrarConfig, setMostrarConfig] = useState(false);

    const cargarResumen = useCallback(async (p) => {
        setCargando(true);
        try {
            setResumen(await sostenibilidadService.huellaCarbono(p || null));
        } catch {
            toast.error('Error al calcular la huella de carbono');
        } finally {
            setCargando(false);
        }
    }, []);

    const cargarRecomendaciones = useCallback(async (p) => {
        setCargandoRecos(true);
        setRecos(null);
        try {
            setRecos(await sostenibilidadService.recomendacionesIA(p || null));
        } catch {
            setRecos({ disponible: false, mensaje: 'No se pudo cargar las recomendaciones de IA.', recomendaciones: [] });
        } finally {
            setCargandoRecos(false);
        }
    }, []);

    const cargarIntensidad = useCallback(async (p) => {
        try {
            setIntensidad(await sostenibilidadService.intensidad(p || null));
        } catch {
            setIntensidad([]);
        }
    }, []);

    useEffect(() => {
        sostenibilidadService.periodos().then(setPeriodos).catch(() => setPeriodos([]));
        cargarResumen('');
        cargarRecomendaciones('');
        cargarIntensidad('');
    }, [cargarResumen, cargarRecomendaciones, cargarIntensidad]);

    const handlePeriodoChange = (e) => {
        const p = e.target.value;
        setPeriodo(p);
        cargarResumen(p);
        cargarRecomendaciones(p);
        cargarIntensidad(p);
    };

    const intensidadConDatos = intensidad.filter(
        (it) => it.intensidad_energetica_kwh_m2 != null || it.intensidad_hidrica_litros_colaborador_dia != null || it.residuos_kg_colaborador != null
    );

    return (
        <Layout>
            <div className="p-6">
                <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Leaf className="h-7 w-7 text-green-600" />
                            Sostenibilidad
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Huella de carbono, costos e indicadores de eficiencia a partir de los consumos de energía,
                            agua, gas, combustible y residuos.
                        </p>
                    </div>
                    <select
                        value={periodo}
                        onChange={handlePeriodoChange}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                        <option value="">Todo el histórico</option>
                        {periodos.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                </div>

                {cargando && (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                    </div>
                )}

                {!cargando && resumen && (
                    <>
                        {/* Nivel 1: Impacto Global */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <p className="text-sm text-gray-500">Huella de carbono total</p>
                                <p className="text-3xl font-bold text-green-700 mt-1">{resumen.total_co2_ton.toLocaleString('es-PE')} ton CO2e</p>
                                <p className="text-xs text-gray-400 mt-1">{resumen.total_co2_kg.toLocaleString('es-PE')} kg CO2e</p>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <p className="text-sm text-gray-500 flex items-center gap-1"><DollarSign className="h-4 w-4" /> Costo total</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">S/. {resumen.total_costo.toLocaleString('es-PE')}</p>
                                <p className="text-xs text-gray-400 mt-1">Energía + agua + gas + combustible + residuos</p>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <p className="text-sm text-gray-500 flex items-center gap-1"><Recycle className="h-4 w-4" /> Tasa de valorización</p>
                                <p className="text-3xl font-bold text-teal-700 mt-1">
                                    {resumen.economia_circular.tasa_valorizacion_pct != null ? `${resumen.economia_circular.tasa_valorizacion_pct}%` : '—'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    {resumen.economia_circular.residuos_kg_reciclado.toLocaleString('es-PE')} kg reciclados de {resumen.economia_circular.residuos_kg_total.toLocaleString('es-PE')} kg
                                </p>
                            </div>
                        </div>

                        {/* Por Alcance GHG Protocol */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
                            <h2 className="text-base font-semibold text-gray-700 mb-4">Por Alcance (GHG Protocol)</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {resumen.por_alcance.map((a) => (
                                    <div key={a.alcance} className="border border-gray-100 rounded-lg p-3">
                                        <p className="text-xs font-semibold text-gray-500">{a.label}</p>
                                        <p className="text-xs text-gray-400 mb-1">{ALCANCE_DESC[a.alcance]}</p>
                                        <p className="text-lg font-bold text-green-700">{a.co2_kg.toLocaleString('es-PE')} kg CO2e</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Análisis y recomendaciones de IA (los números son calculados por el
                            backend; la IA solo los interpreta y recomienda) */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                            <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-green-600" />
                                Análisis de IA
                            </h2>

                            {cargandoRecos && (
                                <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                                    Analizando los indicadores con IA...
                                </div>
                            )}

                            {!cargandoRecos && recos && !recos.disponible && (
                                <p className="text-sm text-gray-400 py-2">{recos.mensaje}</p>
                            )}

                            {!cargandoRecos && recos?.disponible && recos.mensaje && (
                                <p className="text-sm text-gray-400 py-2">{recos.mensaje}</p>
                            )}

                            {!cargandoRecos && recos?.disponible && recos.resumen_ia && (
                                <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">{recos.resumen_ia}</p>
                            )}

                            {!cargandoRecos && recos?.disponible && recos.recomendaciones.length > 0 && (
                                <>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Recomendaciones</h3>
                                    <ul className="space-y-2">
                                        {recos.recomendaciones.map((r, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                <span className="text-green-600 font-bold mt-0.5">•</span>
                                                <span>{r}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <Factory className="h-5 w-5 text-gray-400" />
                                    Por tipo de servicio
                                </h2>
                                {resumen.por_tipo.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-6">Sin datos de consumo para este período.</p>
                                )}
                                {resumen.por_tipo.map((item) => (
                                    <div key={item.tipo} className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0">
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">{TIPO_LABEL[item.tipo] || item.tipo}</p>
                                            <p className="text-xs text-gray-400">{item.cantidad.toLocaleString('es-PE')} {item.unidad} · factor {item.factor_emision} {item.factor_unidad}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-green-700">{item.co2_kg.toLocaleString('es-PE')} kg CO2e</p>
                                            <p className="text-xs text-gray-400">S/. {item.costo.toLocaleString('es-PE')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-gray-400" />
                                    Por almacén
                                </h2>
                                {resumen.por_almacen.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-6">Sin datos de consumo para este período.</p>
                                )}
                                {resumen.por_almacen.map((item) => (
                                    <div key={item.almacen} className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0">
                                        <p className="text-sm font-medium text-gray-800">{item.almacen}</p>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-green-700">{item.co2_kg.toLocaleString('es-PE')} kg CO2e</p>
                                            <p className="text-xs text-gray-400">S/. {item.costo.toLocaleString('es-PE')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Nivel 2: Eficiencia/Intensidad Operativa */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <h2 className="text-base font-semibold text-gray-700 flex items-center gap-2">
                                    <Gauge className="h-5 w-5 text-gray-400" />
                                    Indicadores de Eficiencia por Almacén
                                </h2>
                                {isAdmin() && (
                                    <button
                                        onClick={() => setMostrarConfig(true)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50"
                                    >
                                        <Settings2 className="h-3.5 w-3.5" /> Configurar área / colaboradores
                                    </button>
                                )}
                            </div>
                            {intensidadConDatos.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-6">
                                    Configura el área (m²) y colaboradores por almacén para ver estos indicadores.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 border-b border-gray-100">
                                                <th className="py-2 pr-4">Almacén</th>
                                                <th className="py-2 pr-4">kWh / m²</th>
                                                <th className="py-2 pr-4">Litros agua / colaborador / día</th>
                                                <th className="py-2 pr-4">Kg residuos / colaborador</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {intensidadConDatos.map((it) => (
                                                <tr key={it.almacen} className="border-b border-gray-50 last:border-0">
                                                    <td className="py-2 pr-4 font-medium text-gray-800">{it.almacen}</td>
                                                    <td className="py-2 pr-4">{it.intensidad_energetica_kwh_m2 ?? '—'}</td>
                                                    <td className="py-2 pr-4">{it.intensidad_hidrica_litros_colaborador_dia ?? '—'}</td>
                                                    <td className="py-2 pr-4">{it.residuos_kg_colaborador ?? '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {mostrarConfig && (
                    <ConfiguracionIntensidadModal
                        onCerrar={() => setMostrarConfig(false)}
                        onGuardado={() => cargarIntensidad(periodo)}
                    />
                )}
            </div>
        </Layout>
    );
};

export default SostenibilidadPage;
